// eslint-disable-next-line no-unused-vars
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualTooltip } from 'components/virtualTooltip/useVirtualTooltip';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { useHistory } from 'react-router';

const { updateSelection } = WorkspaceActionCreator;

interface IArgs {
	svgRef: React.MutableRefObject<SVGElement>;
	containerRef: React.MutableRefObject<SVGGElement>;
	data: any;
	dimensions: { height: number; width: number };
	options: any;
	VerticalChart;
	HorizontalChart;
	renderer;
}

/**
 *
 * @param svgRef
 * @param data
 * @param orientation
 * @param dimensions
 * @param options
 * @param updateTooltip
 * @param VerticalChart
 * @param HorizontalChart
 * @param renderer
 */
export const useOrientedChart = ({
	svgRef,
	data,
	orientation,
	dimensions,
	options,
	VerticalChart,
	HorizontalChart,
	renderer,
  hideLabels
}) => {
	// context can be null in a dashboard, so disable selection
	const context = useContext(WorkspaceContext);
	let selection = context?.state.transient.selection;
	let dispatch = context?.dispatch;

	// store the chart for use between runs
	const [Chart, setChart] = useState();

	// store the current direction
	const [currentDirection, setCurrentDirection] = useState('horizontal');

	// keep track of selection locally so we use a consistent object for d3's sake
	const localSelection = useRef([]);

	// use history to create links to pages within the chart
	const history = useHistory();

	const navigateTo = (link) => {
		history.push(link);
	};

	// determine the optimal orientation
	const autoOrientation = useMemo(() => {
		//return dimensions.height > dimensions.width ? 'horizontal' : 'vertical';
		return data.properties.reduce((p, s) => Math.max(p,s.length), 1) > 12 ? 'horizontal' : 'vertical';
	}, [data.properties]);

	const { TooltipElement, updateTooltip } = useVirtualTooltip(renderer);

	useEffect(() => {
		const local = orientation === 'auto' ? autoOrientation : orientation;

		const createChart = () => {
			return local === 'vertical'
				? VerticalChart({
						svg: svgRef.current,
						updateTooltip,
						navigateTo,
						updateSelection: dispatch ? (selection) => dispatch(updateSelection(selection)) : () => {},
				  })
				: HorizontalChart({
						svg: svgRef.current,
						updateTooltip,
						navigateTo,
						updateSelection: dispatch ? (selection) => dispatch(updateSelection(selection)) : () => {},
				  });
		};

		if (data && dimensions) {
			let ch = Chart;

			// update selection
			if (selection === null) {
				localSelection.current.length = 0;
			} else {
				localSelection.current.push(selection);
			}

			// the orientation has changed, need to clear out existing svg if we have a chart already
			if (ch && local !== currentDirection) {
				// @ts-ignore
				ch.clear();

				ch = createChart();
				setChart(ch);
				setCurrentDirection(local);
			}

			// if we don't have an existing chart, create one
			if (!ch) {
				ch = createChart();
				setChart(ch);
			}

			// @ts-ignore
			ch.update({ data, dimensions, options, selection: localSelection.current, hideLabels });
		}
	}, [
		Chart,
		HorizontalChart,
		VerticalChart,
		autoOrientation,
		currentDirection,
		data,
		dimensions,
		dispatch,
		options,
		orientation,
		selection,
		svgRef,
		updateTooltip,
		hideLabels
	]);

	return { TooltipElement, chartMethods: Chart };
};
